"use client";

import React, { useState } from "react";
import { useFormContext } from "react-hook-form";
import { ChevronRight, ChevronLeft, Send, CircleCheck, ShieldCheck, Stethoscope, Clock, Lock } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const STEPS = [{
  id: 1,
  title: "Contact Details",
  description: "So we can reach you securely.",
  fields: ["name", "email", "phone"]
}, {
  id: 2,
  title: "Your Symptoms",
  description: "Tell us what you're experiencing.",
  fields: ["issue_summary"]
}, {
  id: 3,
  title: "Insurance & Timing",
  description: "Help us prepare for your review.",
  fields: ["preferred_contact_method", "best_time_to_contact"]
}];
export default function StartYourJourneyToClarity() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="31729" id="check-your-symptoms" ref={ref} className={cn("relative w-full py-20 px-4 md:py-32 overflow-hidden bg-secondary/30", "transition-opacity duration-1000", isVisible ? "opacity-100" : "opacity-0")}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl opacity-70" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-accent/20 rounded-full blur-3xl opacity-60" />
        <div className="absolute top-[40%] left-[20%] w-[300px] h-[300px] bg-blue-100/20 rounded-full blur-2xl opacity-40" />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto">
        <div className="text-center mb-12 space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-2">
            <Lock className="w-3 h-3" />
            HIPAA Compliant & Secure
          </div>
          <h2 className="text-4xl md:text-5xl font-sans font-bold text-foreground tracking-tight">
            Tell Us What You're Experiencing
          </h2>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
            Provide details about your sleep patterns. Our specialists will review your symptoms to help determine the best path to restful, healthy nights.
          </p>
        </div>

        <div className="bg-background/80 backdrop-blur-xl border border-border/50 shadow-xl rounded-2xl overflow-hidden ring-1 ring-black/5">
          <FormProvider formKey="check_symptoms_form_symptoms" formId="check_symptoms_form_symptoms" sectionName="start-your-journey-to-clarity" defaultValues={{
          name: "",
          email: "",
          phone: "",
          issue_summary: "",
          preferred_contact_method: "Aetna",
          best_time_to_contact: ""
        }} validationRules={{
          name: {
            required: "Full name is required"
          },
          email: {
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Please enter a valid email address"
            }
          },
          issue_summary: {
            required: "Please describe your symptoms"
          },
          preferred_contact_method: {
            required: "Please select your insurance provider"
          }
        }} submitText="Request Consultation" successMessage="Your symptoms have been submitted. A sleep specialist will contact you shortly.">
            <MultiStepForm />
          </FormProvider>
        </div>
        
        <div className="mt-8 flex flex-wrap justify-center gap-x-8 gap-y-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-accent" />
            <span>Insurance Accepted</span>
          </div>
          <div className="flex items-center gap-2">
            <Stethoscope className="w-4 h-4 text-accent" />
            <span>Board Certified Specialists</span>
          </div>
          <div className="flex items-center gap-2">
            <CircleCheck className="w-4 h-4 text-accent" />
            <span>Free Consultation</span>
          </div>
        </div>
      </div>
    </section>;
}
function MultiStepForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  const {
    trigger
  } = useFormContext();
  const [currentStep, setCurrentStep] = useState(1);
  const handleNext = async () => {
    const fieldsToValidate = STEPS[currentStep - 1].fields;
    const isValid = await trigger(fieldsToValidate);
    if (isValid) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
    }
  };
  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };
  const progress = currentStep / STEPS.length * 100;
  if (isSubmitted && !submitError) {
    return <div className="p-12 text-center space-y-6 animate-in fade-in zoom-in duration-500 min-h-[400px] flex flex-col items-center justify-center">
        <div className="w-20 h-20 bg-accent/10 text-accent rounded-full flex items-center justify-center mx-auto mb-2 shadow-sm border border-accent/20">
          <CircleCheck className="w-10 h-10" />
        </div>
        <h3 className="text-2xl font-sans font-bold text-foreground">Review In Progress</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          {successMessage}
        </p>
        <div className="pt-4">
          <Button variant="outline" onClick={resetForm} className="rounded-lg border-primary text-primary hover:bg-primary/5">
            Submit Another Request
          </Button>
        </div>
      </div>;
  }
  return <div className="flex flex-col h-full">
      {}
      <div className="bg-muted/30 px-6 py-6 border-b border-border/50">
        <div className="flex justify-between items-end mb-3">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Phase {currentStep} of {STEPS.length}</span>
            <h3 className="text-lg font-semibold text-primary mt-1">{STEPS[currentStep - 1].title}</h3>
          </div>
          <span className="text-sm text-muted-foreground hidden sm:inline-block">{STEPS[currentStep - 1].description}</span>
        </div>
        <Progress value={progress} className="h-1.5 bg-primary/10" />
      </div>

      {}
      <Form onSubmit={handleSubmit} className="p-6 md:p-10 flex-1 flex flex-col">
        <div className="flex-1 min-h-[320px]">
          {}
          {currentStep === 1 && <div className="space-y-6 animate-in slide-in-from-right-4 fade-in duration-300">
              <div className="space-y-1 mb-6">
                <h3 className="text-xl font-semibold text-foreground">Personal Information</h3>
                <p className="text-sm text-muted-foreground">Please provide your details so we can reach out with your results.</p>
              </div>
              
              <FormField control={control} name="name" render={({
            field
          }) => <FormItem>
                    <FormLabel className="text-foreground font-medium">Full Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Jane Doe" {...field} className="bg-background/50 h-12 rounded-lg border-input focus:ring-primary" />
                    </FormControl>
                    <FormDescription>Please enter your name as it appears on your insurance card.</FormDescription>
                    <FormMessage />
                  </FormItem>} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="email" render={({
              field
            }) => <FormItem>
                      <FormLabel className="text-foreground font-medium">Email Address</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="jane.doe@example.com" {...field} className="bg-background/50 h-12 rounded-lg border-input focus:ring-primary" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={control} name="phone" render={({
              field
            }) => <FormItem>
                      <FormLabel className="text-foreground font-medium">Phone Number <span className="text-muted-foreground font-normal">(Optional)</span></FormLabel>
                      <FormControl>
                        <Input type="tel" placeholder="(555) 123-4567" {...field} className="bg-background/50 h-12 rounded-lg border-input focus:ring-primary" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
              </div>
            </div>}

          {}
          {currentStep === 2 && <div className="space-y-6 animate-in slide-in-from-right-4 fade-in duration-300">
              <div className="space-y-1 mb-6">
                <h3 className="text-xl font-semibold text-foreground">Sleep Health Profile</h3>
                <p className="text-sm text-muted-foreground">Detailing your symptoms helps us provide a more accurate assessment.</p>
              </div>

              <FormField control={control} name="issue_summary" render={({
            field
          }) => <FormItem>
                    <FormLabel className="text-foreground font-medium">What Symptoms are You Experiencing?</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Please describe any snoring, gasping for air, morning headaches, or excessive daytime sleepiness." className="min-h-[200px] resize-none bg-background/50 text-base leading-relaxed p-4 rounded-lg border-input focus:ring-primary" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>}

          {}
          {currentStep === 3 && <div className="space-y-6 animate-in slide-in-from-right-4 fade-in duration-300">
              <div className="space-y-1 mb-6">
                <h3 className="text-xl font-semibold text-foreground">Insurance & Availability</h3>
                <p className="text-sm text-muted-foreground">Help us verify your coverage and schedule a convenient time.</p>
              </div>

              <FormField control={control} name="preferred_contact_method" render={({
            field
          }) => <FormItem>
                    <FormLabel className="text-foreground font-medium">Insurance Provider</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="bg-background/50 h-12 rounded-lg border-input focus:ring-primary">
                          <SelectValue placeholder="Select your provider" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Aetna">Aetna</SelectItem>
                        <SelectItem value="Blue Cross Blue Shield">Blue Cross Blue Shield</SelectItem>
                        <SelectItem value="Cigna">Cigna</SelectItem>
                        <SelectItem value="Medicare">Medicare</SelectItem>
                        <SelectItem value="UnitedHealthcare">UnitedHealthcare</SelectItem>
                        <SelectItem value="Other/Self-Pay">Other / Self-Pay</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="best_time_to_contact" render={({
            field
          }) => <FormItem>
                    <FormLabel className="text-foreground font-medium">Best Time for a Consultation <span className="text-muted-foreground font-normal">(Optional)</span></FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Clock className="absolute left-3 top-4 h-4 w-4 text-muted-foreground" />
                        <Input placeholder="e.g., Weekdays 9am – 12pm" {...field} className="pl-10 bg-background/50 h-12 rounded-lg border-input focus:ring-primary" />
                      </div>
                    </FormControl>
                    <FormDescription>Let us know when you're available for a brief introductory call.</FormDescription>
                    <FormMessage />
                  </FormItem>} />
              
              {submitError && <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-lg text-destructive text-sm mt-4 flex items-center gap-2">
                  <span className="font-bold">Error:</span> {submitError}
                </div>}
            </div>}
        </div>

        {}
        <div className="flex justify-between items-center pt-8 mt-2 border-t border-border/50">
          <Button type="button" variant="ghost" onClick={handleBack} disabled={currentStep === 1 || isSubmitting} className={cn("text-muted-foreground hover:text-primary transition-colors font-medium", currentStep === 1 ? "invisible" : "visible")}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          {currentStep < STEPS.length ? <Button type="button" onClick={handleNext} className="min-w-[140px] bg-primary hover:bg-primary/90 text-white rounded-lg shadow-lg shadow-primary/20 transition-all font-semibold">
              Next Step
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button> : <Button type="submit" disabled={isSubmitting} className="min-w-[180px] bg-accent hover:opacity-90 text-white rounded-lg shadow-lg shadow-accent/20 transition-all font-bold">
              {isSubmitting ? "Sending..." : "Get Free Consultation"}
              {!isSubmitting && <Send className="w-4 h-4 ml-2" />}
            </Button>}
        </div>
      </Form>
    </div>;
}